import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

interface TransformationBuilderProps {
  mappingIndex: number;
}

export function TransformationBuilder({ mappingIndex }: TransformationBuilderProps) {
  const {
    register,
    watch,
    control,
    formState: { errors },
  } = useFormContext();

  const transformType = watch(`config.fieldMappings.${mappingIndex}.transformations.0.type`);

  // Get formula error - React Hook Form types don't support dynamic nested error paths well
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const configErrors = errors?.config as any;
  const formulaError = configErrors?.fieldMappings?.[mappingIndex]?.transformations?.[0]?.parameters?.formula;

  return (
    <Box>
      <Stack spacing={2}>
        {/* Conditional parameters */}
        {transformType === 'conditional' && (
          <>
            <Stack direction='row' spacing={2} alignItems='flex-start'>
              <TextField
                {...register(`config.fieldMappings.${mappingIndex}.transformations.0.parameters.matchValue`)}
                label='Match Value'
                placeholder='e.g., active'
                fullWidth
                size='small'
                helperText='Value to match against (after concatenation if applicable)'
              />
              <Controller
                name={`config.fieldMappings.${mappingIndex}.transformations.0.parameters.matchEmpty`}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value || false} />}
                    label='Match Empty'
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </Stack>
            <TextField
              {...register(`config.fieldMappings.${mappingIndex}.transformations.0.parameters.ifMatch`)}
              label='If Match'
              placeholder='e.g., Yes'
              fullWidth
              size='small'
              helperText='Return this value if it matches'
            />
            <TextField
              {...register(`config.fieldMappings.${mappingIndex}.transformations.0.parameters.ifNoMatch`)}
              label='If No Match'
              placeholder='e.g., No'
              fullWidth
              size='small'
              helperText='Return this value if it does not match'
            />
          </>
        )}

        {/* Calculated parameters */}
        {transformType === 'calculated' && (
          <TextField
            {...register(`config.fieldMappings.${mappingIndex}.transformations.0.parameters.formula`, {
              pattern: {
                value: /^[a-zA-Z0-9_\s+\-*/().^%]+$/,
                message:
                  'Formula can only contain letters, numbers, spaces, and math operators (+, -, *, /, ^, %, parentheses)',
              },
              validate: {
                balanced: (value) => {
                  if (!value) {
                    return true;
                  }
                  const open = (value.match(/\(/g) || []).length;
                  const close = (value.match(/\)/g) || []).length;
                  return open === close || 'Parentheses must be balanced';
                },
              },
            })}
            label='Formula'
            placeholder='e.g., value * 1.1 or price + tax'
            fullWidth
            size='small'
            error={!!formulaError}
            helperText={
              formulaError?.message ||
              'Math expression using "value" (current field value) or other column names'
            }
          />
        )}

        {transformType === 'direct' && (
          <Typography variant='body2' color='text.secondary'>
            No parameters needed - value passes through unchanged
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
